import { TextField, InputAdornment, Stack, Button, Box } from '@mui/material';
import React, { useMemo, useRef, useState } from 'react';
import useBundleTranslation from 'i18n';
import { Popup, PopupSettings } from 'components/common/popup/Popup';
import { FormControlChooseIconSvgProps, FormElementControlPropsType } from 'components/common/form/layout/control';
import IconMi from 'components/common/icon/IconMi';
import styles from './ChooseIcon.styles';
import { IconMiNameList } from 'components/common/icon/IconMiData';
import * as MiIcons from 'components/common/icon/IconMiData';
import { FormComponentBuilder } from 'components/common/form/layout';
import { renderToStaticMarkup } from 'react-dom/server';

const convertMiIconToSvg = (iconName: string): string => {
    try {
        // Convert icon name from kebab-case to PascalCase (e.g., 'chevron-first' -> 'ChevronFirst')
        const componentName = iconName
            .split('-')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');

        const IconComponent = (MiIcons as any)[componentName];
        if (!IconComponent) {
            console.error(`MI icon component not found: ${componentName}`);
            return '';
        }

        // Render the SVG component to string with currentColor for dynamic styling
        let svg = renderToStaticMarkup(<IconComponent />);
        // Add fill="currentColor" to allow CSS color control
        svg = svg.replace('<svg', '<svg fill="currentColor"');
        return svg;
    } catch (error) {
        console.error('Error converting MI icon to SVG:', error);
        return '';
    }
};

export default function ChooseIconSvg({
    controlProps: props,
}: FormElementControlPropsType<FormControlChooseIconSvgProps>) {
    const { t } = useBundleTranslation(['components/common/choose_icon']);
    const [isShowPopup, setIsShowPopup] = useState<boolean>(false);
    const [svgValue, setSvgValue] = useState<string>(props.form.hookFormGetValues(props.name) ?? '');
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const iconsContainerRef = useRef<HTMLDivElement | null>(null);

    const resetIconListScroll = () => {
        if (iconsContainerRef.current) {
            iconsContainerRef.current.scrollTo({
                top: 0,
                behavior: 'instant',
            });
        }
    };

    const iconsList = useMemo(() => {
        const filteredIcons = IconMiNameList.filter((item) => {
            return ![
                'avatar',
                'tile-chart',
                'tile',
                'element-preview',
                'full-chart',
                'live-dashboard',
                'viewer',
            ].includes(item);
        });

        resetIconListScroll();

        const searchString = (searchKeyword || '').toLowerCase().trim();
        if (!searchString) {
            return filteredIcons;
        }

        return filteredIcons.filter((item) => item.includes(searchString));
    }, [searchKeyword]);

    const choosePopupSettings: PopupSettings = {
        title: t('popup_title'),
        textOK: t('popup_button'),
        noCancel: true,
    };

    const closePopup = () => {
        setIsShowPopup(false);
        setSearchKeyword('');
        resetIconListScroll();
    };

    const selectIcon = (iconCode: string) => {
        const svg = convertMiIconToSvg(iconCode);
        setSvgValue(svg);
        props.form.hookFormSetValue(props.name, svg, { shouldDirty: true });
        closePopup();
    };

    const removeIcon = () => {
        setSvgValue('');
        props.form.hookFormSetValue(props.name, '', { shouldDirty: true });
    };

    const onSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchKeyword(event.target.value);
    };

    const resetSearch = () => {
        setSearchKeyword('');
    };

    const hasIcon = svgValue !== '';

    return (
        <Box>
            <Stack direction="row" alignItems="center">
                {hasIcon && (
                    <Box
                        sx={{ ...styles.previewIcon, color: 'text.primary', width: '16px' }}
                        className="icon-type--mi"
                        dangerouslySetInnerHTML={{ __html: svgValue }}
                    />
                )}
                <Button
                    onClick={() => setIsShowPopup(true)}
                    variant="outlined"
                    startIcon={hasIcon ? <IconMi icon={'edit-pencil'} fontSize={'16'} /> : undefined}
                >
                    {t(hasIcon ? 'button_filled_state' : 'button_empty_state')}
                </Button>
                {hasIcon && (
                    <Button
                        variant="outlined"
                        sx={{ ml: 1 }}
                        onClick={removeIcon}
                        className={'min-width--icon'}
                        color={'neutral'}
                    >
                        <IconMi icon="delete" fontSize={'16'} />
                    </Button>
                )}
            </Stack>

            <Popup
                settings={choosePopupSettings}
                open={isShowPopup}
                onHide={() => closePopup()}
                onConfirm={() => closePopup()}
                maxWidth="popupMd"
            >
                <Box sx={{ mb: 2 }}>
                    <TextField
                        onChange={onSearch}
                        value={searchKeyword}
                        fullWidth
                        InputProps={{
                            startAdornment: !searchKeyword ? (
                                <InputAdornment position="start">
                                    <IconMi icon="search" fontSize={'16'} sx={{ color: 'primary.main' }} />
                                </InputAdornment>
                            ) : (
                                <InputAdornment
                                    position="start"
                                    onClick={resetSearch}
                                    sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                                >
                                    <IconMi icon="times" fontSize={'16'} sx={{ color: 'primary.main' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>

                <Box sx={styles.iconsContainer} ref={iconsContainerRef}>
                    {iconsList.length === 0 && searchKeyword && (
                        <Box sx={styles.emptySearchMsg}>{t('search_no_data_found_placeholder')}</Box>
                    )}
                    {iconsList.map((item: string) => (
                        <Box onClick={() => selectIcon(item)} key={item} className="icon-type--mi" sx={styles.iconItem}>
                            <IconMi icon={item} />
                        </Box>
                    ))}
                </Box>
            </Popup>
        </Box>
    );
}

export class ChooseIconSvgBuilder extends FormComponentBuilder {
    prepareProps(): FormControlChooseIconSvgProps {
        return {
            ...this.controlProps,
        };
    }
}
